import { ReactNode } from 'react';
import { Stack } from '@optimus-web/core';
import { useDialogForm, UseDialogFormProps } from '../dialogForm';
import { SteppedPrimaryActionsProps } from './components/SteppedPrimaryActions';
import { useSteppedFormComponents } from './components/useSteppedFormComponents';
import { SteppedDialogFormProps, WithSteps } from './types';

interface UseSteppedDialogFormProps<S extends string>
    extends Omit<UseDialogFormProps, 'renderForm' | 'renderPrimaryAction'>,
        WithSteps<S> {
    stepLabels: Record<S, ReactNode>;
    optionalSteps?: Partial<Record<S, boolean>>;
    renderForm: (props: SteppedDialogFormProps<S>) => ReturnType<UseDialogFormProps['renderForm']>;
    renderPrimaryAction?: (
        props: SteppedPrimaryActionsProps<S>
    ) => ReturnType<NonNullable<UseDialogFormProps['renderPrimaryAction']>>;
}

export const useSteppedDialogForm = <S extends string>({
    steps,
    stepLabels,
    optionalSteps,
    onClose,
    renderForm,
    renderPrimaryAction,
    saveLabel,
    submitButtonDataTestId,
    ...dialogFormProps
}: UseSteppedDialogFormProps<S>) => {
    const { StepProgressIndicator, SteppedPrimaryActions, state } = useSteppedFormComponents({ steps });

    return useDialogForm({
        ...dialogFormProps,
        onClose: () => {
            onClose?.();
            state.setCurrentStep(steps[0]);
        },
        renderForm: (renderFormProps) => (
            <Stack spacing="400" vertical fill>
                <StepProgressIndicator stepLabels={stepLabels} optionalSteps={optionalSteps} />
                {renderForm({ ...renderFormProps, ...state, steps })}
            </Stack>
        ),
        renderPrimaryAction: (primaryActionProps) => {
            const steppedPrimaryActionProps = {
                ...primaryActionProps,
                ...state,
                steps,
                saveLabel,
                submitButtonDataTestId,
            };
            return renderPrimaryAction ? (
                renderPrimaryAction(steppedPrimaryActionProps)
            ) : (
                <SteppedPrimaryActions {...steppedPrimaryActionProps} />
            );
        },
    });
};
